import React, { useEffect, useRef, useState } from 'react';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  /** Texto opcional renderizado em destaque (ex: nome do item afetado). */
  highlight?: string;
  /** Variante do botão de confirmação (default = primary; danger = vermelho). */
  variant?: 'default' | 'danger';
  /** Texto do botão de confirmar. */
  confirmLabel?: string;
  /** Texto do botão de cancelar. */
  cancelLabel?: string;
  /** Ícone material symbol exibido no topo (opcional). */
  icon?: string;
  /** Se preenchido, mostra um input. O onConfirm recebe o valor digitado. */
  input?: {
    label: string;
    type?: 'text' | 'date' | 'number';
    placeholder?: string;
    defaultValue?: string;
    required?: boolean;
    pattern?: string;
    title?: string;
  };
  onCancel: () => void;
  onConfirm: (value?: string) => void | Promise<void>;
}

/**
 * Diálogo de confirmação consistente com o design do sistema (glass-panel + slate + primary).
 *
 * Substitui window.confirm() e window.prompt() — sempre prefira este aqui.
 *
 * Suporte a:
 * - Confirmação simples (sem input)
 * - Prompt com input (text/date/number) com validação HTML5
 * - Variante 'danger' para ações destrutivas
 * - Foco automático no input ao abrir
 * - Fechamento por ESC e click no backdrop
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  description,
  highlight,
  variant = 'default',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  icon,
  input,
  onCancel,
  onConfirm,
}) => {
  const [value, setValue] = useState(input?.defaultValue ?? '');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state quando abre
  useEffect(() => {
    if (isOpen) {
      setValue(input?.defaultValue ?? '');
      setSubmitting(false);
      // Foco no input depois do render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, input?.defaultValue]);

  // ESC fecha
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(input ? value : undefined);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBtnClass = variant === 'danger'
    ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30'
    : 'bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20';

  const iconColor = variant === 'danger' ? 'text-rose-400' : 'text-primary';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background-dark/80 backdrop-blur-sm animate-fade-in"
        onClick={onCancel}
      />

      {/* Dialog */}
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md glass-panel bg-background-dark/95 border border-slate-700/50 rounded-2xl shadow-2xl p-7 animate-fade-in-up"
      >
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          {icon && (
            <div className={`flex-shrink-0 p-2.5 rounded-xl bg-slate-800/80 border border-slate-700/50 ${iconColor}`}>
              <span className="material-symbols-outlined text-[22px]">{icon}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-white leading-tight">{title}</h3>
            {highlight && (
              <p className="text-sm font-bold text-primary mt-1 truncate">{highlight}</p>
            )}
          </div>
        </div>

        {description && (
          <p className="text-sm text-slate-400 mb-5 leading-relaxed">{description}</p>
        )}

        {input && (
          <div className="mb-6">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
              {input.label}
            </label>
            <input
              ref={inputRef}
              type={input.type ?? 'text'}
              required={input.required ?? true}
              pattern={input.pattern}
              title={input.title}
              placeholder={input.placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:ring-2 focus:ring-primary/50 focus:border-primary/60 outline-none transition-all invalid:border-rose-500/50"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-800/50 mt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700/50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed ${confirmBtnClass}`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                Aguarde...
              </span>
            ) : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};
